import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { familyQuery, patientKeys } from "@/portals/patient/api/patientQueries";
import type { CaregiverAccess, Dependent, IncomingPatientAccessRequest } from "@/shared/types/family";

// Dependents, caregivers and incoming caregiver requests, from the shared
// query cache (one request for all three). refresh() refetches at once, so
// anything that creates a dependent or sends a caregiver invite sees its own
// change immediately; caregiver-side changes arrive as notifications, which
// invalidate this query (see usePatientLiveQueries). The provider itself
// only holds UI state: which profile is being viewed.
interface FamilyContextValue {
  dependents: Dependent[];
  caregivers: CaregiverAccess[];
  /** Caregiver-initiated requests (Direction A) awaiting this patient's own
   * review — the reverse of the caregiver portal's own incoming invites
   * (see CaregiverPatientsContext.invites, Direction B). */
  incomingPatientRequests: IncomingPatientAccessRequest[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  /** The dependent whose records the patient is currently viewing — null means "my own". */
  activeDependent: Dependent | null;
  /** Switches which profile's dashboard is being viewed; pass null to switch back to "my own". */
  setActiveDependentId: (dependentId: string | null) => void;
}

const FamilyContext = createContext<FamilyContextValue | null>(null);

const EMPTY: never[] = [];

export function FamilyProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const query = useQuery(familyQuery());
  const [activeDependentId, setActiveDependentId] = useState<string | null>(null);
  const dependents = query.data?.dependents ?? EMPTY;
  const activeDependent = dependents.find((dependent) => dependent.id === activeDependentId) ?? null;

  const refresh = useCallback(() => void client.invalidateQueries({ queryKey: patientKeys.family }), [client]);

  const value = useMemo<FamilyContextValue>(
    () => ({
      dependents,
      caregivers: query.data?.caregivers ?? EMPTY,
      incomingPatientRequests: query.data?.incomingPatientRequests ?? EMPTY,
      // Only the very first load shows a spinner — every later refresh swaps
      // the data in place (consumers replace their whole page while loading).
      isLoading: query.isPending,
      error: query.error ? query.error.message || "Couldn't load your family & caregiver settings. Please try again later." : null,
      refresh,
      activeDependent,
      setActiveDependentId,
    }),
    [dependents, query.data, query.isPending, query.error, refresh, activeDependent],
  );

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
}

export function useFamily() {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error("useFamily must be used within FamilyProvider");
  return ctx;
}
